/**
 * Trace GPX et sa géométrie dérivée.
 *
 * Portage de `GpxTrack.kt`. Les caches (points aplatis, distances cumulées,
 * statistiques, profil lissé) sont coûteux à recalculer sur des traces de
 * plusieurs centaines de milliers de points : ils sont invalidés par un
 * compteur de version, incrémenté à chaque réaffectation de `segments`.
 *
 * L'application Android obtient le même résultat en validant les caches par
 * identité de référence, ce qui l'oblige à ne jamais muter `segments` en
 * place. Le compteur de version rend cette contrainte explicite plutôt
 * qu'implicite, et supporte la mutation si elle passe par `setSegments`.
 */

import {
  bboxMargins,
  haversine,
  lowerBound,
  projectOnSegment,
} from './geo'
import {
  calculateElevationWithHysteresis,
  smoothElevations,
  smoothingHalfWindow,
} from './elevation'
import {
  MARKER_TYPE_WAYOUT,
  createCustomMarker,
  type CustomMarker,
  type GpxSourceType,
  type RoutingProfile,
  type TrackPoint,
  type TrackSegment,
  type WayInOut,
  type Waypoint,
} from './types'
import { ROUTING_PROFILES } from './types'

/**
 * Seuil de projection par défaut, en mètres.
 *
 * Distinct des seuils d'accrochage de `SnapToRoad` (20 m / 25 m) : celui-ci
 * répond à « où l'utilisateur a-t-il touché la trace ? », les autres à
 * « sur quoi roule-t-on ? ». Les fusionner casserait l'un ou l'autre.
 */
export const PROJECTION_THRESHOLD_M = 18.0

/**
 * Écart maximal toléré entre l'indice de progression et la position projetée.
 *
 * Au-delà, la position est refusée plutôt que d'accepter un saut vers une
 * autre itération de boucle.
 */
export const MAX_JUMP_M = 2000.0

/** Écart de regroupement des candidats du dialogue « Corriger la position ». */
export const FIX_POSITION_CLUSTER_GAP_M = 200.0

export interface DistanceCandidate {
  /** Abscisse curviligne depuis le début de la trace, en mètres. */
  readonly distance: number
  /** Distance entre le point interrogé et sa projection, en mètres. */
  readonly projDist: number
}

export interface KmMarkerPoint {
  readonly lat: number
  readonly lon: number
  /** Libellé affiché dans la pastille : « 7 » ou « 7,5 ». */
  readonly label: string
  /** Abscisse curviligne brute, en mètres. */
  readonly distance: number
}

export interface RoadbookEntry {
  readonly marker: CustomMarker
  /** Négatif une fois le marqueur dépassé. */
  readonly remainingKm: number
}

interface GeometryCache {
  readonly version: number
  readonly allPoints: readonly TrackPoint[]
  readonly cumulativeDistances: Float64Array
}

interface StatsCache {
  readonly version: number
  readonly totalDistance: number
  readonly gain: number
  readonly loss: number
  readonly minEle: number
  readonly maxEle: number
  readonly hasElevationData: boolean
  readonly profileDistances: readonly number[]
  readonly profileElevations: readonly number[]
  readonly profileRawElevations: readonly number[]
}

export interface TrackInit {
  readonly id: string
  readonly name: string
  readonly segments?: readonly TrackSegment[]
  readonly waypoints?: readonly Waypoint[]
  readonly color?: string
  readonly visible?: boolean
  readonly sourceType?: GpxSourceType
}

/**
 * Redistribue une géométrie à plat dans la découpe d'une autre.
 *
 * Les opérations qui recalculent les altitudes rendent un tableau de points
 * dans l'ordre, sans savoir où passaient les frontières de `<trkseg>`. Les
 * réassigner en un segment unique aplatirait la trace : la découpe fait partie
 * de ce que le fichier dit — coupure d'enregistrement, tunnel — et Android la
 * reconstruit à sa place (`ElevationController.kt`). Deux exports de la même
 * trace depuis les deux applications doivent rendre la même structure.
 *
 * Le nombre de points doit correspondre exactement ; sinon la découpe ne
 * s'applique plus et un segment unique est rendu, ce qui vaut mieux qu'un
 * découpage arbitraire.
 */
/**
 * Longueur d'une géométrie, en mètres, sans passer par une `GpxTrack`.
 *
 * Le dialogue de sélection doit annoncer la distance de chaque `<trk>` d'un
 * fichier **avant** qu'aucune trace n'existe. Les frontières de segments ne
 * comptent pas, comme partout ailleurs.
 */
export function totalDistanceOf(segments: readonly TrackSegment[]): number {
  let total = 0
  for (const segment of segments) {
    const points = segment.points
    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1]!
      const curr = points[i]!
      total += haversine(prev.lat, prev.lon, curr.lat, curr.lon)
    }
  }
  return total
}

export function resegmentLike(
  shape: readonly TrackSegment[],
  points: readonly TrackPoint[],
): TrackSegment[] {
  const total = shape.reduce((sum, segment) => sum + segment.points.length, 0)
  if (total !== points.length) return [{ points: [...points] }]

  const segments: TrackSegment[] = []
  let offset = 0
  for (const segment of shape) {
    const length = segment.points.length
    segments.push({ points: points.slice(offset, offset + length) })
    offset += length
  }
  return segments
}

/**
 * Un candidat par passage, le plus proche de chacun.
 *
 * Sur une boucle ou un aller-retour, un même point du terrain se projette à
 * plusieurs abscisses curvilignes de la trace. Les projections voisines — les
 * segments qui se suivent autour du point, un virage serré, un rond-point —
 * appartiennent au même passage et se fondent en une ; au-delà de
 * `clusterGapMeters`, c'est un passage de plus.
 *
 * Partagé par la projection point par point et par la recherche des radars,
 * qui doivent séparer les passages de la même façon : deux découpes
 * indépendantes finiraient par diverger sans que rien ne le dise.
 *
 * Référence : `GpxTrack.projectPointAll`.
 */
export function clusterPassages(
  candidates: readonly DistanceCandidate[],
  clusterGapMeters: number,
): DistanceCandidate[] {
  if (candidates.length === 0) return []

  const sorted = [...candidates].sort((a, b) => a.distance - b.distance)

  const passages: DistanceCandidate[] = []
  let clusterBest = sorted[0]!
  let clusterEnd = sorted[0]!.distance

  for (let i = 1; i < sorted.length; i++) {
    const candidate = sorted[i]!

    if (candidate.distance - clusterEnd > clusterGapMeters) {
      passages.push(clusterBest)
      clusterBest = candidate
    } else if (candidate.projDist < clusterBest.projDist) {
      // Comparaison stricte : à égalité, la première projection du passage
      // garde la place, celle que la trace rencontre en premier.
      clusterBest = candidate
    }

    // Mis à jour à chaque candidat, pas seulement aux ruptures : un long
    // passage continu ne doit pas être scindé par sa seule longueur.
    clusterEnd = candidate.distance
  }

  passages.push(clusterBest)
  return passages
}

export class GpxTrack {
  readonly id: string
  name: string
  color: string
  visible: boolean
  sourceType: GpxSourceType

  /** Distance du départ réel depuis le début de la trace, en mètres. */
  realStartDistance = 0

  /** Intervalle propre à la trace ; 0 signifie « suivre le réglage global ». */
  kmMarkerInterval = 0

  /** Compter les points kilométriques depuis le départ fictif plutôt que réel. */
  kmMarkersFromFictiveStart = false

  /**
   * Avancement d'une tâche en cours sur la trace, de 0 à 1.
   *
   * Affiché sur sa ligne dans le tiroir plutôt qu'en bandeau : la récupération
   * d'altitude comme le routage durent plusieurs secondes, et un bandeau
   * persistant gênerait l'usage de la carte pendant ce temps.
   */
  taskProgress: number | null = null

  /** Libellé de la tâche en cours, déjà traduit. */
  taskLabel: string | null = null

  /**
   * Un calcul d'itinéraire est en cours sur cette trace.
   *
   * Distingué de `taskProgress`, que la récupération d'altitude alimente
   * aussi : seul le routage s'annule d'un appui sur la ligne du tiroir, comme
   * sur Android où le spinner d'altitude ne porte aucun écouteur
   * (`GpxListAdapter`). Sans ce drapeau, la ligne offrirait une annulation
   * qu'une des deux tâches ne sait pas honorer.
   */
  isRouting = false

  isRouted = false
  routingProfile: RoutingProfile = ROUTING_PROFILES.CAR

  /**
   * La géométrie a-t-elle été remplacée depuis la lecture du fichier ?
   *
   * C'est ce que l'export interroge pour savoir s'il doit réécrire les
   * `<trkseg>` du fichier source ou laisser passer ses octets. Faux, les
   * points d'origine ressortent **avec leurs extensions tierces** — cardio,
   * cadence — que le modèle ne porte pas et qu'une réécriture perdrait.
   *
   * Deux opérations seulement le posent, ce sont les deux seules qui
   * remplacent la géométrie : le routage et la récupération d'altitude. Le
   * retour au tracé d'origine le repose à faux.
   *
   * **Toute nouvelle opération qui appelle `setSegments` doit le poser.** Rien
   * ne le vérifie, et l'oublier fait exporter l'ancienne géométrie en silence
   * — un défaut qui ne se voit qu'en rouvrant le fichier exporté.
   *
   * Jamais persisté : la restauration repose la valeur en rejouant le cache
   * d'itinéraire ou d'altitude.
   */
  geometryModified = false

  /**
   * Le fichier source déclarait déjà cette trace comme routée.
   *
   * Ses `<trkseg>` sont alors le résultat d'un calcul, pas un tracé d'origine :
   * « Revenir aux points d'origine » n'aurait rien à relire, et c'est le
   * recalcul qui est proposé à la place. Jamais persisté — la relecture du
   * fichier le repose.
   */
  fileDeclaresRouted = false

  customMarkers: CustomMarker[] = []
  wayInOuts: WayInOut[] = []

  /** Géométries routées des way in/out, indexées par identifiant de way. */
  wayRoutes = new Map<string, readonly TrackPoint[]>()

  /**
   * Points d'intérêt du fichier que le commutateur masque.
   *
   * Les masquer ne les **retire** plus du modèle : ils quittent `waypoints`,
   * donc la carte, mais restent ici. Sans cela, un export fait alors que le
   * commutateur est fermé ne contenait aucun `<wpt>` — le fichier rendu
   * n'était plus celui qu'on avait reçu, pour un réglage d'affichage.
   *
   * Le rejeu du fichier source n'en a pas besoin : les `<wpt>` y passent octet
   * pour octet, qu'ils soient affichés ou non. C'est le repli `writeGpx` qui
   * les lit, lui qui reconstruit le document depuis le seul modèle.
   */
  hiddenFileWaypoints: readonly Waypoint[] = []

  /** Index du `<trk>` d'origine dans le fichier source. */
  fileTrackIndex = 0
  includeFileTracks = true

  /**
   * Les points d'intérêt du fichier appartiennent-ils à cette trace ?
   *
   * Drapeau de **propriétaire** : les points d'intérêt d'un fichier sont
   * rattachés à sa première trace, elle seule les porte — sans quoi la
   * restauration les dupliquerait sur chaque trace d'un fichier à plusieurs
   * `<trk>`. C'est aussi ce que l'interrupteur des réglages bascule, la
   * remise à `true` relisant le fichier source (`GpxTrack.includeFileWaypoints`,
   * `TrackSettingsController.setFileWaypointsShown`).
   */
  includeFileWaypoints = true

  #segments: readonly TrackSegment[]
  #waypoints: readonly Waypoint[]
  #version = 0

  #geometryCache: GeometryCache | null = null
  #statsCache: StatsCache | null = null
  #gainLossMemo = new Map<string, { gain: number; loss: number }>()

  constructor(init: TrackInit) {
    this.id = init.id
    this.name = init.name
    this.#segments = init.segments ?? []
    this.#waypoints = init.waypoints ?? []
    this.color = init.color ?? '#E53935'
    this.visible = init.visible ?? true
    this.sourceType = init.sourceType ?? 'TRK'
  }

  /* ---------------------------------------------------------------------
   * Géométrie
   * ------------------------------------------------------------------ */

  get segments(): readonly TrackSegment[] {
    return this.#segments
  }

  /**
   * Numéro de version de la géométrie, incrémenté par `setSegments`.
   *
   * Exposé pour que les caches extérieurs à la classe — entités MapLibre,
   * distances des points d'intérêt — se valident au même signal que les caches
   * internes, plutôt que de recalculer à chaque redessin.
   */
  get geometryVersion(): number {
    return this.#version
  }

  /**
   * Remplace la géométrie et invalide tous les caches dérivés.
   *
   * Unique point d'entrée pour modifier la géométrie : le routage, le retour
   * aux waypoints et l'application d'un profil d'altitude passent tous par ici.
   */
  setSegments(segments: readonly TrackSegment[]): void {
    this.#segments = segments
    this.#version++
    this.#geometryCache = null
    this.#statsCache = null
    this.#gainLossMemo.clear()
  }

  get waypoints(): readonly Waypoint[] {
    return this.#waypoints
  }

  setWaypoints(waypoints: readonly Waypoint[]): void {
    this.#waypoints = waypoints
  }

  #geometry(): GeometryCache {
    const cached = this.#geometryCache
    if (cached && cached.version === this.#version) {
      return cached
    }

    const allPoints: TrackPoint[] = []
    for (const segment of this.#segments) {
      for (const point of segment.points) {
        allPoints.push(point)
      }
    }

    // Les frontières de segments ne contribuent pas à la distance : un GPX
    // découpé en plusieurs `<trkseg>` (coupure d'enregistrement, tunnel) ne
    // doit pas gagner de kilomètres au niveau des coupures.
    const cumulative = new Float64Array(allPoints.length)
    let flatIndex = 0
    let cumulativeDistance = 0

    for (const segment of this.#segments) {
      const points = segment.points
      for (let i = 0; i < points.length; i++) {
        if (i > 0) {
          const prev = points[i - 1]!
          const curr = points[i]!
          cumulativeDistance += haversine(prev.lat, prev.lon, curr.lat, curr.lon)
        }
        cumulative[flatIndex] = cumulativeDistance
        flatIndex++
      }
    }

    const geometry: GeometryCache = {
      version: this.#version,
      allPoints,
      cumulativeDistances: cumulative,
    }
    this.#geometryCache = geometry
    return geometry
  }

  get allPoints(): readonly TrackPoint[] {
    return this.#geometry().allPoints
  }

  get cumulativeDistances(): Float64Array {
    return this.#geometry().cumulativeDistances
  }

  /* ---------------------------------------------------------------------
   * Statistiques et profil
   * ------------------------------------------------------------------ */

  #stats(): StatsCache {
    const cached = this.#statsCache
    if (cached && cached.version === this.#version) {
      return cached
    }

    const { allPoints, cumulativeDistances } = this.#geometry()
    const totalDistance =
      allPoints.length > 0 ? cumulativeDistances[allPoints.length - 1]! : 0

    const profileDistances: number[] = []
    const rawElevations: number[] = []
    let minEle = Number.POSITIVE_INFINITY
    let maxEle = Number.NEGATIVE_INFINITY

    for (let i = 0; i < allPoints.length; i++) {
      const ele = allPoints[i]!.ele
      if (ele === null) continue
      profileDistances.push(cumulativeDistances[i]!)
      rawElevations.push(ele)
      if (ele < minEle) minEle = ele
      if (ele > maxEle) maxEle = ele
    }

    // Une trace dont toutes les altitudes sont identiques (souvent `ele=0`
    // partout) n'a pas de profil exploitable : on la traite comme dépourvue
    // d'altitude plutôt que d'afficher un profil plat et un D+ nul.
    const hasElevationData = rawElevations.length > 0 && minEle < maxEle

    let gain = 0
    let loss = 0
    let profileElevations: readonly number[] = rawElevations

    if (hasElevationData) {
      const half = smoothingHalfWindow(rawElevations, profileDistances)
      profileElevations = smoothElevations(rawElevations, half)
      const result = calculateElevationWithHysteresis(profileElevations)
      gain = result.gain
      loss = result.loss
    }

    const stats: StatsCache = {
      version: this.#version,
      totalDistance,
      gain,
      loss,
      minEle: hasElevationData ? minEle : 0,
      maxEle: hasElevationData ? maxEle : 0,
      hasElevationData,
      profileDistances,
      profileElevations,
      profileRawElevations: rawElevations,
    }
    this.#statsCache = stats
    return stats
  }

  /** Longueur totale de la trace, en mètres. */
  get totalDistance(): number {
    return this.#stats().totalDistance
  }

  get elevationGain(): number {
    return this.#stats().gain
  }

  get elevationLoss(): number {
    return this.#stats().loss
  }

  get minElevation(): number {
    return this.#stats().minEle
  }

  get maxElevation(): number {
    return this.#stats().maxEle
  }

  get hasElevationData(): boolean {
    return this.#stats().hasElevationData
  }

  /** Distances des points porteurs d'altitude, alignées sur `profileElevations`. */
  get profileDistances(): readonly number[] {
    return this.#stats().profileDistances
  }

  /**
   * Altitudes lissées : dénivelé cumulé et profil du HUD.
   *
   * Le lissage est **local** — il rabote les extrêmes de quelques dixièmes de
   * mètre —, si bien que cette série ne va pas jusqu'à `minElevation` et
   * `maxElevation`, qui sont ceux de la série brute. Chaque vue trace donc la
   * série dont elle affiche les extrêmes, comme le fait Android : le HUD
   * recalcule les siens sur sa fenêtre lissée, la fiche d'infos prend la série
   * brute ci-dessous.
   */
  get profileElevations(): readonly number[] {
    return this.#stats().profileElevations
  }

  /**
   * Altitudes brutes, dans l'ordre de `profileDistances`.
   *
   * C'est la série dont `minElevation` et `maxElevation` sont exactement les
   * extrêmes, et celle que trace la fiche d'infos — `ElevationProfileView`
   * consomme de même `profileRawElevations` et met à l'échelle sur ses propres
   * `min()`/`max()`.
   */
  get profileRawElevations(): readonly number[] {
    return this.#stats().profileRawElevations
  }

  /**
   * Dénivelé cumulé à partir d'une abscisse curviligne donnée.
   *
   * Utilisé par la section « Depuis le départ réel » de la fiche d'infos.
   */
  gainLossFromDistance(fromDistance: number): { gain: number; loss: number } {
    const key = `${this.#version}:${fromDistance}`
    const memo = this.#gainLossMemo.get(key)
    if (memo) return memo

    const { profileDistances, profileElevations, hasElevationData } = this.#stats()

    let result: { gain: number; loss: number }
    if (!hasElevationData) {
      result = { gain: 0, loss: 0 }
    } else {
      const startIndex = lowerBound(profileDistances, fromDistance)
      result = calculateElevationWithHysteresis(
        profileElevations,
        undefined,
        startIndex,
      )
    }

    this.#gainLossMemo.set(key, result)
    return result
  }

  /**
   * Longueur qu'il reste à parcourir depuis le départ réel.
   *
   * Sans départ réel, c'est la longueur entière — la ligne du tiroir et la
   * fiche d'infos peuvent donc l'appeler sans condition. Le plancher à zéro
   * reprend le `coerceAtLeast(0.0)` d'Android : un départ réel posé au-delà de
   * la fin de la trace, ce qu'un calcul d'itinéraire raccourcissant la
   * géométrie peut produire, donnerait sinon une distance négative.
   */
  /**
   * La trace a-t-elle un départ réel ?
   *
   * Zéro n'en est pas un : c'est la valeur d'une trace qui n'en a jamais reçu,
   * et le début de la trace lui sert alors de départ. La question se posait
   * au même mot à quatre endroits — couches de la carte, tiroir, pose par
   * appui long —, et une trace n'a qu'un seul départ réel : c'est cette
   * réponse qui décide s'il faut proposer de déplacer l'existant.
   */
  get hasRealStart(): boolean {
    return this.realStartDistance > 0
  }

  get distanceFromRealStart(): number {
    if (this.realStartDistance <= 0) return this.totalDistance
    return Math.max(this.totalDistance - this.realStartDistance, 0)
  }

  /* ---------------------------------------------------------------------
   * Convention de comptage des points kilométriques
   * ------------------------------------------------------------------ */

  /**
   * Décalage entre l'abscisse curviligne brute et les km affichés.
   *
   * Quand un départ réel est configuré, les marqueurs sont comptés depuis lui,
   * sauf si l'utilisateur demande explicitement de compter depuis le départ
   * fictif. Cette convention est partagée par les points kilométriques, les
   * marqueurs personnalisés, le roadbook, l'indicateur kilométrique, le radar
   * d'alerte et les way in/out — d'où son unicité.
   */
  get markerDistanceOffset(): number {
    return this.realStartDistance > 0 && !this.kmMarkersFromFictiveStart
      ? this.realStartDistance
      : 0
  }

  /** Abscisse curviligne brute (m) → km affiché. Peut être négatif. */
  rawDistanceToMarkerKm(rawDistanceMeters: number): number {
    return (rawDistanceMeters - this.markerDistanceOffset) / 1000
  }

  /** Km affiché → abscisse curviligne brute (m). */
  markerKmToRawDistance(km: number): number {
    return km * 1000 + this.markerDistanceOffset
  }

  /* ---------------------------------------------------------------------
   * Projection d'un point sur la trace
   * ------------------------------------------------------------------ */

  /**
   * Tous les points de la trace passant à moins de `threshold` du point donné.
   *
   * Un pré-filtre par boîte englobante par segment élimine l'immense majorité
   * des paires sans aucun appel trigonométrique — indispensable pour rester
   * réactif sur des traces de 200 000 points.
   */
  collectCandidates(
    lat: number,
    lon: number,
    threshold = PROJECTION_THRESHOLD_M,
  ): DistanceCandidate[] {
    const candidates: DistanceCandidate[] = []
    const { cumulativeDistances } = this.#geometry()
    const { latMargin, lonMargin } = bboxMargins(lat, threshold)

    let flatBase = 0

    for (const segment of this.#segments) {
      const points = segment.points

      for (let i = 0; i < points.length - 1; i++) {
        const p1 = points[i]!
        const p2 = points[i + 1]!

        const minLat = Math.min(p1.lat, p2.lat) - latMargin
        const maxLat = Math.max(p1.lat, p2.lat) + latMargin
        if (lat < minLat || lat > maxLat) continue

        const minLon = Math.min(p1.lon, p2.lon) - lonMargin
        const maxLon = Math.max(p1.lon, p2.lon) + lonMargin
        if (lon < minLon || lon > maxLon) continue

        const proj = projectOnSegment(lat, lon, p1.lat, p1.lon, p2.lat, p2.lon)
        const projDist = haversine(lat, lon, proj.lat, proj.lon)

        if (projDist <= threshold) {
          const from = cumulativeDistances[flatBase + i]!
          const to = cumulativeDistances[flatBase + i + 1]!
          candidates.push({
            distance: from + proj.t * (to - from),
            projDist,
          })
        }
      }

      // Incrémenté même pour les segments de moins de deux points, sans quoi
      // l'indexation à plat se désaligne des distances cumulées.
      flatBase += points.length
    }

    return candidates
  }

  /** Projection la plus proche, ou `null` si la trace est hors de portée. */
  projectPoint(
    lat: number,
    lon: number,
    maxDistanceMeters = PROJECTION_THRESHOLD_M,
  ): DistanceCandidate | null {
    const candidates = this.collectCandidates(lat, lon, maxDistanceMeters)
    if (candidates.length === 0) return null

    let best = candidates[0]!
    for (const candidate of candidates) {
      if (candidate.projDist < best.projDist) best = candidate
    }
    return best
  }

  /**
   * Un candidat par passage de la trace près du point.
   *
   * Sur une boucle ou un aller-retour, un même point de l'écran correspond à
   * plusieurs points kilométriques : cette méthode les sépare pour que
   * l'utilisateur puisse choisir lequel il visait.
   */
  projectPointAll(
    lat: number,
    lon: number,
    maxDistanceMeters = PROJECTION_THRESHOLD_M,
    clusterGapMeters = maxDistanceMeters * 2,
  ): DistanceCandidate[] {
    return clusterPassages(
      this.collectCandidates(lat, lon, maxDistanceMeters),
      clusterGapMeters,
    )
  }

  /** Candidats proposés par le dialogue « Corriger la position ». */
  allDistanceCandidates(lat: number, lon: number): DistanceCandidate[] {
    return this.projectPointAll(
      lat,
      lon,
      PROJECTION_THRESHOLD_M,
      FIX_POSITION_CLUSTER_GAP_M,
    )
  }

  /**
   * Abscisse curviligne de la position, en mètres, ou `null` si hors trace.
   *
   * `lastKnownDistance` lève l'ambiguïté sur les boucles : c'est le critère
   * **primaire** de sélection, pas un simple départage. Un écart supérieur à
   * `MAX_JUMP_M` fait rejeter la position — mieux vaut ne rien afficher que
   * de se verrouiller sur le mauvais passage.
   */
  distanceAtPoint(
    lat: number,
    lon: number,
    lastKnownDistance?: number | null,
  ): number | null {
    const candidates = this.collectCandidates(lat, lon)
    if (candidates.length === 0) return null

    let best: DistanceCandidate

    if (lastKnownDistance != null && candidates.length > 1) {
      best = candidates[0]!
      let bestDelta = Math.abs(best.distance - lastKnownDistance)
      for (const candidate of candidates) {
        const delta = Math.abs(candidate.distance - lastKnownDistance)
        if (delta < bestDelta) {
          best = candidate
          bestDelta = delta
        }
      }
    } else {
      best = candidates[0]!
      for (const candidate of candidates) {
        if (candidate.projDist < best.projDist) best = candidate
      }
    }

    if (
      lastKnownDistance != null &&
      Math.abs(best.distance - lastKnownDistance) > MAX_JUMP_M
    ) {
      return null
    }

    return best.distance
  }

  /**
   * Point interpolé à une abscisse curviligne donnée.
   *
   * Interpolation linéaire en lat/lon/altitude — l'écart avec un grand cercle
   * est négligeable à l'échelle d'un segment GPX.
   */
  pointAtDistance(targetDistance: number): TrackPoint | null {
    const { allPoints, cumulativeDistances } = this.#geometry()
    if (allPoints.length === 0) return null

    if (targetDistance <= 0) return allPoints[0]!

    const last = allPoints.length - 1
    if (targetDistance >= cumulativeDistances[last]!) return allPoints[last]!

    const lo = lowerBound(cumulativeDistances, targetDistance)
    if (lo < 1) return allPoints[0]!

    const prev = allPoints[lo - 1]!
    const curr = allPoints[lo]!
    const span = cumulativeDistances[lo]! - cumulativeDistances[lo - 1]!

    // Points confondus ou frontière de segment : rien à interpoler.
    if (span <= 0) return curr

    const ratio = (targetDistance - cumulativeDistances[lo - 1]!) / span

    return {
      lat: prev.lat + ratio * (curr.lat - prev.lat),
      lon: prev.lon + ratio * (curr.lon - prev.lon),
      ele:
        prev.ele !== null && curr.ele !== null
          ? prev.ele + ratio * (curr.ele - prev.ele)
          : null,
      time: null,
      name: null,
    }
  }

  /* ---------------------------------------------------------------------
   * Points kilométriques
   * ------------------------------------------------------------------ */

  /**
   * Positions des pastilles kilométriques le long de la trace.
   *
   * L'intervalle propre à la trace prime sur le réglage global.
   */
  generateKmMarkerPoints(globalIntervalKm: number): KmMarkerPoint[] {
    const intervalKm =
      this.kmMarkerInterval > 0 ? this.kmMarkerInterval : globalIntervalKm
    if (intervalKm <= 0) return []

    const interval = intervalKm * 1000
    const startDist = this.markerDistanceOffset
    const { cumulativeDistances } = this.#geometry()

    const result: KmMarkerPoint[] = []
    let nextTarget = startDist + interval
    let flatBase = 0

    for (const segment of this.#segments) {
      const points = segment.points

      for (let i = 1; i < points.length; i++) {
        const cumulative = cumulativeDistances[flatBase + i - 1]!
        const span = cumulativeDistances[flatBase + i]! - cumulative
        if (span <= 0) continue

        const p1 = points[i - 1]!
        const p2 = points[i]!

        while (cumulative + span >= nextTarget) {
          const ratio = (nextTarget - cumulative) / span
          result.push({
            lat: p1.lat + ratio * (p2.lat - p1.lat),
            lon: p1.lon + ratio * (p2.lon - p1.lon),
            label: formatKmLabel((nextTarget - startDist) / 1000),
            distance: nextTarget,
          })
          nextTarget += interval
        }
      }

      flatBase += points.length
    }

    return result
  }

  /* ---------------------------------------------------------------------
   * Roadbook et marqueurs dérivés
   * ------------------------------------------------------------------ */

  /**
   * Prochains marqueurs à afficher dans le roadbook.
   *
   * Un marqueur reste visible un court moment après avoir été dépassé
   * (`remainingKm` négatif) : sur le terrain, on veut pouvoir vérifier qu'on
   * vient bien de passer le point attendu.
   */
  getRoadbookEntries(
    currentDistanceMeters: number,
    maxEntries: number,
    disappearDistanceKm = 1.0,
  ): RoadbookEntry[] {
    const currentKm = this.rawDistanceToMarkerKm(currentDistanceMeters)

    return this.customMarkers
      .filter((marker) => marker.visible)
      .map((marker) => ({
        marker,
        remainingKm: marker.distanceKm - currentKm,
      }))
      .filter((entry) => entry.remainingKm > -disappearDistanceKm)
      .sort((a, b) => a.remainingKm - b.remainingKm)
      .slice(0, maxEntries)
  }

  /**
   * Recrée les marqueurs dérivés des way in/out.
   *
   * Idempotent : les anciens sont retirés avant reconstruction. Les way in/out
   * sont la source de vérité, ces marqueurs n'existent que pour l'affichage
   * dans le roadbook et le radar d'alerte.
   */
  rebuildWayOutMarkers(symbol: string): void {
    this.customMarkers = this.customMarkers.filter(
      (marker) => marker.type !== MARKER_TYPE_WAYOUT,
    )

    for (const way of this.wayInOuts) {
      this.customMarkers.push(
        createCustomMarker({
          type: MARKER_TYPE_WAYOUT,
          name: way.name,
          symbol,
          distanceKm: way.exitKm,
          // Format neutre volontaire : le modèle ne connaît pas la langue
          // active, et cette description est aussi écrite dans le GPX.
          description: `→ km ${way.entryKm.toFixed(2)}`,
          visible: way.visible,
          wayId: way.id,
        }),
      )
    }
  }
}

/**
 * L'interrupteur « afficher les points d'intérêt » a-t-il lieu d'être ?
 *
 * Trois gardes, reprises de `TrackSettingsController.buildFileWaypointsSwitch` :
 *
 * - la trace doit porter ses segments — sur une trace de points seuls, les
 *   masquer la viderait, et sa restauration au démarrage n'aurait plus rien à
 *   reconstruire ;
 * - le fichier doit avoir eu des points d'intérêt. Ils sont là, ou bien la
 *   trace les a déclinés à l'import — c'est ce que dit `includeFileWaypoints`
 *   à `false`, qui n'est posé que sur un fichier qui en contenait ;
 * - et seule la trace **propriétaire** peut les afficher. Sans cette garde,
 *   l'interrupteur apparaissait sur toutes les traces d'un fichier à plusieurs
 *   `<trk>`, et l'activer sur une non-propriétaire dupliquait les points de
 *   façon persistée.
 *
 * La propriété se mesure par **comptage de références sur le fichier source**,
 * et non plus par `fileTrackIndex === 0`. L'ancien critère faisait disparaître
 * l'interrupteur — sans aucun recours — sur une trace importée en « trace
 * seule » depuis la troisième `<trk>` d'un fichier : elle est bien la seule à
 * référencer ce fichier, donc bien propriétaire de ses points, mais son index
 * n'était pas nul. Le repli sur l'index ne sert qu'aux traces sans copie
 * source, qui n'ont de toute façon rien à relire.
 */
export function canToggleFileWaypoints(
  track: {
    readonly waypoints: readonly unknown[]
    readonly includeFileTracks: boolean
    readonly includeFileWaypoints: boolean
    readonly fileTrackIndex: number
  },
  /**
   * Une autre trace partage-t-elle le même fichier source ?
   *
   * `null` quand la trace n'a pas de copie source : on retombe alors sur
   * l'index, faute de mieux.
   */
  sharedWithOthers: boolean | null = null,
): boolean {
  if (!track.includeFileTracks) return false

  const owns =
    sharedWithOthers === null ? track.fileTrackIndex === 0 : !sharedWithOthers

  return track.waypoints.length > 0 || (!track.includeFileWaypoints && owns)
}

/**
 * La trace qu'on importe doit-elle porter les points d'intérêt du fichier ?
 *
 * Ils appartiennent au **fichier**, pas à un `<trk>` : une seule trace les
 * porte, sans quoi ils se dessinent en double et deux traces se croient
 * propriétaires.
 *
 * Trois conditions, et la troisième est celle qu'on oublie : réimporter le même
 * fichier pour en prendre une autre trace — le mode d'emploi assumé depuis
 * qu'un GPX ne donne plus qu'une trace — présente une trace qui est bien la
 * première de *son* import, mais dont le fichier a déjà un porteur en
 * bibliothèque.
 */
export function ownsFileWaypointsAtImport(
  indexInImport: number,
  includeWaypoints: boolean,
  alreadyOwnedInLibrary: boolean,
): boolean {
  return indexInImport === 0 && includeWaypoints && !alreadyOwnedInLibrary
}

/**
 * Le propriétaire des points d'intérêt, tel qu'un enregistrement le rend.
 *
 * Deux réparations, faites à la relecture parce que le fichier vient d'y être
 * réanalysé — c'est le seul moment où l'on sait ce qu'il contient :
 *
 * - `showWaypoints`, retiré, portait l'affichage séparément de l'appartenance.
 *   Un enregistrement qui masquait ses points devient un enregistrement qui ne
 *   les prend pas : l'utilisateur voit la même chose, et l'interrupteur les
 *   ramène ;
 * - un fichier **sans** point d'intérêt ne pose pas la question à l'import, et
 *   la réponse enregistrée valait `false` — ce qui, sous la règle ci-dessus,
 *   ferait apparaître un interrupteur qui ne trouverait rien à charger.
 */
export function restoredFileWaypoints(
  record: { readonly includeFileWaypoints: boolean; readonly showWaypoints?: boolean },
  fileWaypointCount: number,
): boolean {
  if (fileWaypointCount === 0) return true
  return record.includeFileWaypoints && record.showWaypoints !== false
}

/**
 * Libellé d'une pastille kilométrique.
 *
 * L'arrondi est comparé plutôt que le modulo : un intervalle saisi au curseur
 * produit des multiples très légèrement inférieurs à l'entier (0,7 × 1000 =
 * 699,99998), qui s'afficheraient « 7,0 » au lieu de « 7 ».
 */
export function formatKmLabel(distKm: number): string {
  const rounded = Math.round(distKm)
  return Math.abs(distKm - rounded) < 0.001
    ? String(rounded)
    : distKm.toFixed(1)
}
